import React from 'react';
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';

export const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

const STATUS = {
  ok:   { label: 'On track',        color: 'success', icon: <CheckCircleIcon /> },
  near: { label: 'Nearly used',     color: 'warning', icon: <WarningAmberIcon /> },
  over: { label: 'Over budget',     color: 'error',   icon: <ErrorIcon /> },
};

// Budget status (from the server's monitor): on track / nearly used (80%+) / over budget
export function BudgetStatusChip({ status, size = 'small' }) {
  const s = STATUS[status] || STATUS.ok;
  return <Chip size={size} icon={s.icon} label={s.label} color={s.color} variant={status === 'ok' ? 'outlined' : 'filled'} />;
}

// Spent against budget, with the amount left (or over)
export function BudgetBar({ spent, budget, status, usedPercent, dense = false }) {
  const pct = Math.min(100, Math.max(0, usedPercent ?? (budget > 0 ? (spent / budget) * 100 : 0)));
  const left = budget - spent;
  return (
    <Box sx={{ minWidth: dense ? 120 : 0 }}>
      <LinearProgress
        variant="determinate" value={pct}
        color={STATUS[status]?.color || 'success'}
        sx={{ height: dense ? 6 : 8, borderRadius: 4 }}
      />
      <Stack direction="row" justifyContent="space-between" mt={0.25}>
        <Typography variant="caption" color="text.secondary">
          {inr(spent)} of {inr(budget)}{usedPercent != null ? ` · ${usedPercent}%` : ''}
        </Typography>
        {!dense && (
          <Typography variant="caption" color={left < 0 ? 'error.main' : 'text.secondary'} fontWeight={left < 0 ? 700 : 400}>
            {left < 0 ? `${inr(-left)} over` : `${inr(left)} left`}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

// Who started a budget category
export function budgetSourceText(cat) {
  const role = cat.creator?.role?.name;
  if (cat.fromService && !cat.creator) return 'Started from the services selected';
  if (role === 'system_admin') return `${cat.fromService ? 'Service' : 'Custom'} budget added by the admin`;
  if (role === 'matchmaker') return `${cat.fromService ? 'Service' : 'Custom'} budget added by the matchmaker`;
  return cat.fromService ? 'Service budget added by the family' : 'Custom budget added by the family';
}
